import React, { useCallback, useEffect, useLayoutEffect, useRef, useState } from "react"

import { Popover } from "@mui/material"

import { KonanJsonView } from "./KonanJsonView"

interface JsonPopperProps {
  id: string
  isOpen: boolean
  onClose: () => void
  anchorEl: HTMLButtonElement | null
  content: Record<string, unknown>
}

/**
 * Internal component that measures content offscreen to determine stable dimensions
 */
function OffscreenMeasurer({ children, onStable }: { children: React.ReactNode; onStable: () => void }): JSX.Element {
  const ref = useRef<HTMLDivElement | null>(null)

  useLayoutEffect(() => {
    const el = ref.current
    if (!el) return
    const ro = new ResizeObserver(([entry]) => {
      const { width, height } = entry?.contentRect ?? { width: 0, height: 0 }
      if (width && height) onStable()
    })
    ro.observe(el)
    return () => ro.disconnect()
  }, [onStable])

  return (
    <div ref={ref} style={{ position: "absolute", left: 0, top: 0, visibility: "hidden" }}>
      {children}
    </div>
  )
}

/**
 * A popover component that displays JSON content with pre-measurement for stable sizing
 */
export function JsonPopper(props: Readonly<JsonPopperProps>): React.ReactElement {
  const { isOpen, onClose, anchorEl, id, content } = props
  const [ready, setReady] = useState(false)

  useEffect(() => {
    if (!isOpen) setReady(false)
  }, [isOpen, content])

  const handleStable = useCallback(() => setReady(true), [])

  return (
    <>
      {isOpen && !ready && (
        <OffscreenMeasurer onStable={handleStable}>
          <KonanJsonView src={content} />
        </OffscreenMeasurer>
      )}

      <Popover
        id={id}
        open={isOpen && ready}
        anchorEl={anchorEl}
        onClose={onClose}
        anchorOrigin={{ vertical: "top", horizontal: "right" }}
        transformOrigin={{ vertical: "bottom", horizontal: "left" }}
        onClick={(e) => e.stopPropagation()}
      >
        <KonanJsonView src={content} />
      </Popover>
    </>
  )
}
